import { useState } from "react";
import {
  ArrowUpRight,
  BookOpen,
  CircleHelp,
  Mic,
  MessageSquare,
  MoreHorizontal,
  Monitor,
  Phone,
  Send,
  Settings,
  Share2,
  Smartphone,
  Square,
  X,
} from "lucide-react";
import { isPageLive } from "@/pages/UserHub/GeniePages/geniePageStatus";
import { ConversationWelcome } from "@/pages/agent-interfaces/live-agent/components/ConversationWelcome";
import { cn } from "@/lib/utils";
import { GenieAvatar } from "@/components/GenieAvatar";
import { resolveCtaColors } from "@/lib/ctaButtonStyle";
import { isDarkBackground, widgetBackgroundToCss } from "@/lib/widgetBackgroundPresets";
import { deriveSecondary } from "@/pages/agent-interfaces/live-agent/components/GenieChatInputWeb";
import type { Agent } from "@/types/agent/agent";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import type { FullPageStyle, FullPageAvatarAlign } from "@/types/ui/branding";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface FullPagePreviewProps {
  genie: Agent;
  primaryColor: string;
  fullPageStyle: FullPageStyle;
  /** Which tab opens by default. Defaults to "start"; pass "chat" when
   *  embedding this preview inside a chat-settings-focused context. */
  defaultTab?: "start" | "talking" | "chat";
}

/** Studio's actual idle-state action row: a "Talk" pill (mic glyph) next to an
 *  outlined "Chat" pill (message-square glyph) — see StudioActionCluster's
 *  not-connected branch, whose Talk colors come from the same
 *  resolveCtaColors call used here. */
const TalkChatButtonRow = ({
  ctaBackground,
  ctaTextColor,
  ctaScale,
  chatColor,
  chatBackgroundColor,
  chatScale,
}: {
  ctaBackground: string;
  ctaTextColor: string;
  ctaScale: number;
  chatColor?: string | null;
  chatBackgroundColor?: string | null;
  chatScale: number;
}) => (
  <div className="flex items-center gap-1.5">
    <div
      className="flex items-center gap-1 rounded-full font-semibold"
      style={{
        padding: `${3 * ctaScale}px ${8 * ctaScale}px`,
        background: ctaBackground,
        color: ctaTextColor,
        fontSize: 7 * ctaScale,
      }}
    >
      <Mic size={8 * ctaScale} strokeWidth={2.25} />
      Talk
    </div>
    <div
      className="flex items-center gap-1 rounded-full border font-semibold"
      style={{
        padding: `${3 * chatScale}px ${8 * chatScale}px`,
        fontSize: 7 * chatScale,
        borderColor: chatColor || "#D1D5DB",
        color: chatColor || "#374151",
        backgroundColor: chatBackgroundColor || undefined,
      }}
    >
      <MessageSquare size={8 * chatScale} strokeWidth={2.25} />
      Chat
    </div>
  </div>
);

/** Studio's connected-state pill — Mic / Chat / Stop / More, tinted brand
 *  background. Mirrors StudioActionCluster's voiceConnected branch. */
const TalkingPill = ({
  brandColor,
  secondaryColor,
  actionColor,
}: {
  brandColor: string;
  secondaryColor?: string | null;
  actionColor: string;
}) => (
  <div
    className="flex items-center gap-1 rounded-full p-1"
    style={{ background: `${brandColor}0d` }}
  >
    <div
      className="flex h-5 w-5 items-center justify-center rounded-full"
      style={{ color: brandColor }}
    >
      <Mic size={11} strokeWidth={2} />
    </div>
    <div
      className="flex h-5 w-5 items-center justify-center rounded-full"
      style={{ color: brandColor }}
    >
      <MessageSquare size={10} strokeWidth={2} />
    </div>
    <div
      className="flex h-5 w-7 items-center justify-center rounded-full"
      style={{
        color: brandColor,
        background: `linear-gradient(135deg, ${brandColor}20, ${secondaryColor || brandColor}14)`,
      }}
    >
      <Square size={9} strokeWidth={2} fill="currentColor" />
    </div>
    <div
      className="flex h-5 w-5 items-center justify-center rounded-full"
      style={{ color: actionColor }}
    >
      <MoreHorizontal size={11} strokeWidth={2.25} />
    </div>
  </div>
);

/** The top-right header row on the voice screen, per StudioHeaderActions:
 *  Call (phones, when the genie has a number), Show chat (during a
 *  conversation with the chat closed), the Links & Resources book (phones,
 *  and desktop while the chat is on screen), Support page (when the genie's
 *  help page is live; an icon beside Call) and Share. */
const HeaderRowMock = ({
  device,
  talking,
  chatOpen,
  primaryColor,
  secondaryColor,
  hasPhone,
  hasSupportPage,
  showDetailsLink,
}: {
  device: "desktop" | "mobile";
  talking: boolean;
  chatOpen: boolean;
  primaryColor: string;
  secondaryColor?: string | null;
  hasPhone: boolean;
  hasSupportPage: boolean;
  showDetailsLink: boolean;
}) => {
  const showCall = device === "mobile" && hasPhone;
  const iconClass = "flex h-5 w-5 items-center justify-center text-gray-600";

  return (
    <div className="absolute right-2.5 top-2.5 z-10 flex items-center gap-1">
      {showCall && (
        <span
          className="flex items-center gap-1 rounded-full px-2 py-1 text-[8px] font-semibold text-white"
          style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor || primaryColor})` }}
        >
          <Phone size={8} strokeWidth={2.25} />
          Call
        </span>
      )}
      {talking && !chatOpen && (
        <span className={cn(iconClass, device === "desktop" && "w-auto gap-1 px-1 text-[8px]")}>
          <MessageSquare size={9} />
          {device === "desktop" && "Show chat"}
        </span>
      )}
      {showDetailsLink && (
        <span className={cn(iconClass, device === "desktop" && "w-auto gap-1 px-1 text-[8px] font-medium")}>
          <BookOpen size={9} />
          {device === "desktop" && "Links & resources"}
        </span>
      )}
      {hasSupportPage &&
        (showCall ? (
          <span className={iconClass}>
            <CircleHelp size={9} />
          </span>
        ) : (
          <span className="flex items-center gap-0.5 px-1 text-[8px] font-medium text-gray-600">
            Support page <ArrowUpRight size={8} />
          </span>
        ))}
      <span
        className="flex h-6 w-6 items-center justify-center rounded-full"
        style={{ backgroundColor: `${primaryColor}14`, color: primaryColor }}
      >
        <Share2 size={9} strokeWidth={2} />
      </span>
    </div>
  );
};

/** "Chat view" — the website chat's empty state as the real surfaces render
 *  it. Desktop mirrors GenieConversationCanvas: a slim identity header (logo,
 *  name, "Online") over the welcome. Mobile mirrors the chat drawer, whose
 *  header is a control strip with no name. The welcome itself is the real
 *  ConversationWelcome, zoomed down to the preview's scale. */
const ChatViewMock = ({
  device,
  primaryColor,
  accentColor,
  chatStyle,
  agentName,
  logoUrl,
  actionButtons,
  textColor,
  darkSurface,
}: {
  device: "desktop" | "mobile";
  primaryColor: string;
  accentColor: string;
  chatStyle: NonNullable<FullPageStyle["chatStyle"]> | null | undefined;
  agentName?: string | null;
  logoUrl?: string | null;
  actionButtons: FullPageStyle["actionButtons"];
  textColor?: string | null;
  darkSurface?: boolean;
}) => {
  const logoChip = (
    <span
      className="flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg"
      style={{ background: `linear-gradient(135deg, ${primaryColor}1a, ${accentColor}1a)` }}
    >
      {logoUrl ? <img src={logoUrl} alt="" className="h-4 w-4 object-contain" /> : <GenieIcon name="genie-lamp" size={16} />}
    </span>
  );

  return (
    <div className="flex h-full flex-col">
      {device === "desktop" ? (
        <div className="flex items-center gap-2 border-b border-[#F1F4F9] bg-white/95 px-3 py-2">
          {logoChip}
          <div className="min-w-0 flex-1">
            <p className="truncate text-[10px] font-semibold leading-tight text-[#222B45]">{agentName || "Genie"}</p>
            <span className="mt-0.5 flex items-center gap-1 text-[8px] leading-tight text-[#64748B]">
              <span className="h-1 w-1 rounded-full" style={{ backgroundColor: primaryColor }} />
              Online
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <Settings size={10} />
            <MoreHorizontal size={11} />
            <X size={10} />
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2 px-3 py-2 text-gray-500">
          <Settings size={10} />
          <X size={10} />
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div style={{ zoom: device === "desktop" ? 0.6 : 0.55 }}>
          <ConversationWelcome
            brandColor={primaryColor}
            secondaryColor={accentColor}
            agentName={agentName ?? undefined}
            logoUrl={logoUrl}
            chatStyle={chatStyle}
            textColor={textColor}
            darkSurface={darkSurface}
          />
        </div>
      </div>

      <div className="px-3 pb-3">
        <div
          className="rounded-full"
          style={
            chatStyle?.hideBorder
              ? undefined
              : {
                  padding: "1px",
                  background: `linear-gradient(to right, ${chatStyle?.borderColor || primaryColor}, ${chatStyle?.borderColor2 || chatStyle?.borderColor || accentColor})`,
                }
          }
        >
          <div
            className="flex items-center gap-1.5 rounded-full px-2 py-1.5"
            style={{ backgroundColor: chatStyle?.inputBackgroundColor || "white", boxShadow: "0 1px 3px rgba(15,23,42,0.12)" }}
          >
            <div
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px]"
              style={{ backgroundColor: `${chatStyle?.plusButtonColor || primaryColor}14`, color: chatStyle?.plusButtonColor || primaryColor }}
            >
              +
            </div>
            <span className="flex-1 truncate text-[10px]" style={{ color: chatStyle?.placeholderColor || "#9CA3AF" }}>
              Type a message…
            </span>
            {/* Empty input → the send button's idle state (GenieChatInputWeb). */}
            <div
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
              style={{
                backgroundColor: chatStyle?.sendButtonUseActionStyle
                  ? actionButtons?.idleBackgroundColor || `${primaryColor}14`
                  : `${primaryColor}14`,
                color: (chatStyle?.sendButtonUseActionStyle && actionButtons?.iconColor) || "#9CA3AF",
              }}
            >
              <Send size={9} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Miniature mock of the Studio full-page layout — Links & Resources tab,
 * avatar column, welcome text, idle Talk/Chat buttons — reflecting live
 * (unsaved) fullPageStyle edits so changes in the Full Page Appearance tab
 * are visible before Save. Deliberately approximate (not pixel-identical to
 * StudioDefaultBranch's real layout): it exists to show relative
 * positioning/visibility/color choices, not to be a pixel-perfect renderer.
 *
 * Like the real stage, the avatar holds the centre of the frame and the
 * welcome text and buttons arrange themselves above and below it. The genie's
 * name is not part of the voice stage; it only appears in the chat header.
 *
 * Desktop and Mobile are genuinely different renders, not a scaled-down
 * desktop view: on mobile, StudioDefaultBranch puts the Links & Resources
 * entry in the header row as an icon (rail-position fields only apply on
 * desktop), and a "below avatar" welcome sits between the avatar and the
 * buttons, since the buttons render after the stage there.
 */
const FullPagePreview = ({ genie, primaryColor, fullPageStyle, defaultTab = "start" }: FullPagePreviewProps) => {
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [previewTab, setPreviewTab] = useState<"start" | "talking" | "chat">(defaultTab);
  const avatarUrl = genie.branding?.logoUrl || genie.branding?.customAvatarUrl;
  const accentColor = genie.branding?.secondaryColor || deriveSecondary(primaryColor);

  const layout = fullPageStyle.layout;
  const linksResourcesPosition = layout?.linksResourcesPosition ?? "right";
  const avatarAlign: FullPageAvatarAlign = layout?.avatarAlign ?? "center";
  const welcomePosition = layout?.welcomePosition ?? (fullPageStyle.hideWelcomeMessage ? "hidden" : "above-avatar");
  const welcomeHidden = welcomePosition === "hidden";
  const welcomeBelow = welcomePosition === "below-avatar";
  const actionColor = fullPageStyle.actionButtons?.iconColor || primaryColor;

  const pageBg = widgetBackgroundToCss(fullPageStyle.background, "#F9FAFC");
  // Same resolution as StudioActionCluster's idle "Talk" pill: brand →
  // secondary gradient by default, solid when only one custom color is set.
  const cta = resolveCtaColors({
    brandColor: primaryColor,
    gradientEnd: genie.branding?.secondaryColor || DEFAULT_BRANDING.secondaryColor,
    connected: false,
    gradient: true,
    colorOverride: fullPageStyle.ctaButton?.color,
    colorOverride2: fullPageStyle.ctaButton?.color2,
    textColorOverride: fullPageStyle.ctaButton?.textColor,
  });
  const ctaScale = fullPageStyle.ctaButton?.sizeScale ?? 1;
  const chatColor = fullPageStyle.chatButton?.color;
  const chatBackgroundColor = fullPageStyle.chatButton?.backgroundColor;
  const chatScale = fullPageStyle.chatButton?.sizeScale ?? 1;

  const avatarJustify = avatarAlign === "left" ? "flex-start" : avatarAlign === "right" ? "flex-end" : "center";

  // Chat view: white unless a page background is set, as fullPageChatAppearance resolves it.
  const chatBg = fullPageStyle.background ? widgetBackgroundToCss(fullPageStyle.background, "#FFFFFF") : { backgroundColor: "#FFFFFF" };
  const chatView = (device: "desktop" | "mobile") => (
    <div className="absolute inset-0" style={chatBg}>
      <ChatViewMock
        device={device}
        primaryColor={primaryColor}
        accentColor={accentColor}
        chatStyle={fullPageStyle.chatStyle}
        agentName={genie.name}
        logoUrl={genie.branding?.logoUrl}
        actionButtons={fullPageStyle.actionButtons}
        textColor={fullPageStyle.welcomeTextColor}
        darkSurface={isDarkBackground(fullPageStyle.background)}
      />
    </div>
  );

  const welcomeNode = !welcomeHidden && (
    <p
      className={cn("text-[9px] text-center max-w-[140px]", welcomeBelow ? "mt-2" : "mb-2")}
      style={{ color: fullPageStyle.welcomeTextColor || "#666666" }}
    >
      Hey! I'm {genie.name}
    </p>
  );

  const buttonsNode =
    previewTab === "talking" ? (
      <TalkingPill brandColor={primaryColor} secondaryColor={genie.branding?.secondaryColor} actionColor={actionColor} />
    ) : (
      <TalkChatButtonRow
        ctaBackground={cta.background}
        ctaTextColor={cta.color}
        ctaScale={ctaScale}
        chatColor={chatColor}
        chatBackgroundColor={chatBackgroundColor}
        chatScale={chatScale}
      />
    );

  // Real gating: the Links & Resources entry only exists once connected.
  const showDetails = previewTab === "talking" && linksResourcesPosition !== "hidden";
  // A voice conversation opens the chat on desktop as soon as it connects
  // (useStudioConversationCore), which moves Links & resources into the header
  // row; the chat itself is shown by the Chat view tab, not here.
  const headerRow = (frame: "desktop" | "mobile") => (
    <HeaderRowMock
      device={frame}
      talking={previewTab === "talking"}
      chatOpen={frame === "desktop" && previewTab === "talking"}
      primaryColor={primaryColor}
      secondaryColor={genie.branding?.secondaryColor}
      hasPhone={!!genie.phone?.number}
      hasSupportPage={isPageLive(genie)}
      showDetailsLink={showDetails}
    />
  );

  // Real VoiceAgentAvatar (standalone/non-embed): a 400px soft-glow wrapper
  // around a 320px GenieAvatar (0.8 ratio) — see VoiceAgentAvatar.tsx. This
  // preview's frame is a fixed 340px tall, nowhere near a real page's height,
  // so the wrapper is scaled down to keep the same ~45% proportion of frame
  // height the real avatar occupies of page height, rather than reusing the
  // raw 400px figure.
  const avatarWrapperPx = Math.round(150 * (fullPageStyle.avatarSizeScale ?? 1));
  const avatarNode = (
    <div
      className="flex-shrink-0 flex items-center justify-center rounded-full"
      style={{
        width: avatarWrapperPx,
        height: avatarWrapperPx,
        background: `${primaryColor}18`,
        boxShadow: `0 0 24px ${primaryColor}33`,
      }}
    >
      <GenieAvatar
        color={primaryColor}
        avatarBackgroundOpacity={genie.branding?.avatarBackgroundOpacity}
        size={Math.round(avatarWrapperPx * 0.8)}
        customAvatarUrl={avatarUrl}
        coverImageUrl={fullPageStyle.avatarImageUrl}
        showLogoOverlay={fullPageStyle.avatarShowLogoOverlay}
      />
    </div>
  );

  /** Avatar pinned to the frame's vertical centre; `above`/`below` stack
   *  against it, as useStageAnchorCentering does on the real page. */
  const anchoredStage = (
    above: React.ReactNode,
    below: React.ReactNode,
    paddingClass: string,
    align: React.CSSProperties["alignItems"],
  ) => (
    <div
      className={cn("absolute inset-0 grid", paddingClass)}
      style={{ gridTemplateRows: "minmax(0, 1fr) auto minmax(0, 1fr)", justifyItems: align }}
    >
      <div className="flex min-h-0 flex-col justify-end overflow-hidden" style={{ alignItems: align }}>
        {above}
      </div>
      {avatarNode}
      <div className="flex min-h-0 flex-col overflow-hidden" style={{ alignItems: align }}>
        {below}
      </div>
    </div>
  );

  const voiceStage = (paddingClass: string) =>
    anchoredStage(
      !welcomeBelow && welcomeNode,
      <div className="mt-2 flex flex-col" style={{ alignItems: avatarJustify }}>
        {buttonsNode}
        {welcomeBelow && welcomeNode}
      </div>,
      paddingClass,
      avatarJustify,
    );

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Live Preview</h3>
        <div className="flex items-center rounded-lg bg-gray-100 p-0.5">
          <button
            type="button"
            onClick={() => setDevice("desktop")}
            title="Desktop preview"
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
              device === "desktop" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700",
            )}
          >
            <Monitor className="h-3 w-3" />
            Desktop
          </button>
          <button
            type="button"
            onClick={() => setDevice("mobile")}
            title="Mobile preview"
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
              device === "mobile" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700",
            )}
          >
            <Smartphone className="h-3 w-3" />
            Mobile
          </button>
        </div>
      </div>

      <div className="mb-3 flex items-center rounded-lg bg-gray-100 p-0.5 w-fit">
        <button
          type="button"
          onClick={() => setPreviewTab("start")}
          className={cn(
            "px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
            previewTab === "start" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700",
          )}
        >
          Start screen
        </button>
        <button
          type="button"
          onClick={() => setPreviewTab("talking")}
          className={cn(
            "px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
            previewTab === "talking" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700",
          )}
        >
          Talking
        </button>
        <button
          type="button"
          onClick={() => setPreviewTab("chat")}
          className={cn(
            "px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
            previewTab === "chat" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700",
          )}
        >
          Chat view
        </button>
      </div>

      {device === "desktop" ? (
        <div className="relative overflow-hidden rounded-lg shadow-lg" style={{ height: 340, ...pageBg }}>
          {previewTab === "chat" ? (
            chatView("desktop")
          ) : (
            <>
              {voiceStage("px-6 py-3")}
              {headerRow("desktop")}
            </>
          )}
        </div>
      ) : (
        // Mobile — phone-shaped frame. Links & Resources is an icon in the
        // header row here; rail-position fields don't apply on this viewport.
        // The avatar column stays centred (avatar alignment is desktop-only).
        <div className="flex justify-center">
          <div
            className="relative overflow-hidden rounded-[28px] border-[6px] border-gray-800 shadow-lg"
            style={{ width: 200, height: 400, ...pageBg }}
          >
            {previewTab === "chat" ? (
              chatView("mobile")
            ) : (
              <>
                {anchoredStage(
                  !welcomeBelow && welcomeNode,
                  <div className="flex flex-col items-center">
                    {welcomeBelow && welcomeNode}
                    <div className="mt-2">{buttonsNode}</div>
                  </div>,
                  "px-4 py-11",
                  "center",
                )}

                {headerRow("mobile")}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FullPagePreview;
